/**
 * client/src/lib/removal-hold-duties-api.ts
 *
 * The guardian's DURABLE removal-hold duty inbox (#3009) — every live 48-hour
 * hold on an account this user actively guards, with an honest per-row answer
 * to "can I stop this one right now".
 *
 * WHY A PULL FEED AND NOT THE PUSH. `guardian:removal_hold_veto_request` is a
 * WebSocket frame that reaches OPEN SOCKETS ONLY and persists nothing: a
 * guardian with the app closed got nothing and had no row to come back to. That
 * is exactly why the hold card was forbidden from promising a guardian veto
 * (ledger #128 sub-note; PR #3076 stripped six such strings). This read is the
 * missing half — a plain authenticated pull of live server state, reload-safe
 * and offline-safe, on the same pattern as `handshake-duties-api.ts`.
 *
 * Backed by `GET /api/security/removal-holds/guardian-duties`.
 */

import { apiRequest } from "@/lib/queryClient";

/** Why an otherwise-eligible guardian cannot stop this one. */
export type GuardianVetoIneligibility =
  | "conflicted"
  | "guardians_off"
  | "account_paused"
  | "guardian_account_paused";

export interface GuardianRemovalHoldDuty {
  holdId: string;
  wardDisplayName: string;
  openedAt: string;
  expiresAt: string;
  canVeto: boolean;
  ineligibleReason: GuardianVetoIneligibility | null;
  /** False = the `relationship_only` basis: no shared questions are asked. */
  questionsEnrolled: boolean;
}

export interface GuardianRemovalHoldDutiesResponse {
  duties: GuardianRemovalHoldDuty[];
}

/** Shared by the section and by the removal-hold push listener's invalidation,
 *  imported rather than retyped so the two cannot drift. */
export const REMOVAL_HOLD_DUTIES_QUERY_KEY = ["security", "removal-holds", "guardian-duties"] as const;

export function getGuardianRemovalHoldDuties(): Promise<GuardianRemovalHoldDutiesResponse> {
  return apiRequest<GuardianRemovalHoldDutiesResponse>(
    "/api/security/removal-holds/guardian-duties",
  );
}

/** Cast one guardian's veto. `mutualChallengeAnswers`/`mutualChallengeNonce`
 *  are omitted for a relationship that carries no enrolled question set — the
 *  server decides which applies, and a client that invented three values
 *  nothing compares would be putting a made-up proof on the wire. */
export function vetoRemovalHold(
  holdId: string,
  attempt?: { mutualChallengeAnswers: string[]; mutualChallengeNonce: string },
): Promise<{ success: true; holdId: string; status: string }> {
  return apiRequest<{ success: true; holdId: string; status: string }>(
    `/api/security/removal-holds/${encodeURIComponent(holdId)}/veto`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(attempt ?? {}),
    },
  );
}

/** Plain-English reason a duty row is read-only. Never names the target — the
 *  server does not disclose it, and inventing detail here would be a guess. */
export function vetoIneligibilityCopy(reason: GuardianVetoIneligibility): string {
  switch (reason) {
    case "conflicted":
      return "You can't stop this one — you started it, it affects your account, or it is about your own guardianship.";
    case "guardians_off":
      return "Guardian actions are switched off right now, so this can't be stopped from the app.";
    case "account_paused":
      return "The account you protect is frozen right now, so nothing can be changed until it unfreezes. The 48 hours pause too — the deadline moves back by exactly the frozen time.";
    case "guardian_account_paused":
      // The guardian's OWN freeze, which is a different sentence in every way
      // that matters: the ward's account is fine and the 48 hours keep running.
      //
      // THE DROPPED CLAUSE (security review). This used to end "…and anyone else
      // who protects them can still stop it" — a promise about a SECOND
      // PRINCIPAL that this row cannot know exists. The duty feed is scoped to
      // the caller's own guardianships and carries no circle size, so in a
      // one-guardian circle the sentence invented a co-guardian, and the ward
      // (who can see nothing here) was left with nobody. Say only what is true
      // of this caller.
      return "Your own account is frozen right now, so you can't stop this from here until yours unfreezes. The 48 hours keep running.";
  }
}
